/**
 * ServerStats.jsx — Collapsible server performance widget for the sidebar
 *
 * Displays CPU, RAM, and Disk usage with color-coded progress bars.
 * Polls /api/system-stats every 10 seconds.
 * Shows AI token usage and cost below server stats.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useRBAC } from '../../hooks/useRBAC';
import { useSettings } from '../../hooks/useSettings';
import {
  Box,
  Typography,
  LinearProgress,
  Collapse,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Monitor as MonitorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Token as TokenIcon,
} from '@mui/icons-material';

function barColor(pct) {
  if (pct >= 80) return '#f87171';   // red
  if (pct >= 60) return '#facc15';   // yellow
  return '#4ade80';                   // green
}

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function formatCost(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function StatBar({ label, value, total, unit }) {
  const pct = typeof value === 'number' ? value : 0;
  const color = barColor(pct);
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.8)', fontFamily: 'monospace' }}>
          {total ? `${total}${unit}` : `${pct}%`}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        sx={{
          height: 5,
          borderRadius: 1,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': {
            bgcolor: color,
            borderRadius: 1,
          },
        }}
      />
    </Box>
  );
}

export default function ServerStats({ expanded: sidebarExpanded }) {
  const [open, setOpen] = useState(true);
  const { hasAccess } = useRBAC();
  const { data: settings } = useSettings();
  // AI spend is company money; the server only serves it at payroll:full.
  const canSeeSpend = hasAccess('payroll', 'full', settings);

  const { data: stats } = useQuery({
    queryKey: ['system-stats'],
    queryFn: () => api.get('/system-stats'),
    refetchInterval: 10_000,
    staleTime: 8_000,
    retry: 1,
  });

  const { data: tokenUsage } = useQuery({
    queryKey: ['token-usage'],
    queryFn: () => api.get('/system-stats/token-usage'),
    enabled: canSeeSpend,
    refetchInterval: 60_000,
    staleTime: 50_000,
    retry: 1,
  });

  if (!stats) return null;

  const labelSx = { fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)' };
  const valueSx = { fontSize: '0.65rem', color: 'rgba(255,255,255,0.8)', fontFamily: 'monospace' };

  // Collapsed sidebar — just show an icon with tooltip
  if (!sidebarExpanded) {
    const tokenSummary = canSeeSpend && tokenUsage?.month
      ? ` | AI ${formatTokens(tokenUsage.month.totalTokens)} tok ${formatCost(tokenUsage.month.costCents)}`
      : '';
    const summary = `CPU ${stats.cpu}% | RAM ${stats.memPercent}% | Disk ${stats.diskPercent}%${tokenSummary}`;
    return (
      <Tooltip title={summary} placement="right" arrow>
        <IconButton sx={{ color: 'rgba(255,255,255,0.5)', mx: 'auto', display: 'flex' }}>
          <MonitorIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ px: 1, py: 0.5 }}>
      {/* Toggle header */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 0.5,
          px: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <MonitorIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }} />
          <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Server
          </Typography>
        </Box>
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.4)' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.4)' }} />
        )}
      </Box>

      {/* Stats content */}
      <Collapse in={open}>
        <Box sx={{ pt: 0.5, px: 0.5 }}>
          <StatBar label="CPU" value={stats.cpu} />
          <StatBar
            label="RAM"
            value={stats.memPercent}
            total={`${stats.memUsed}/${stats.memTotal}`}
            unit="G"
          />
          <StatBar
            label="Disk"
            value={stats.diskPercent}
            total={`${stats.diskUsed}/${stats.diskTotal}`}
            unit="G"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)' }}>
              {stats.hostname}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)' }}>
              up {formatUptime(stats.uptime)}
            </Typography>
          </Box>

          {/* AI Token Usage */}
          {canSeeSpend && tokenUsage?.month && (
            <>
              <Divider sx={{ my: 0.75, borderColor: 'rgba(255,255,255,0.08)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <TokenIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }} />
                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  AI Usage (month)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={labelSx}>Tokens</Typography>
                <Typography sx={valueSx}>{formatTokens(tokenUsage.month.totalTokens)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={labelSx}>Cost</Typography>
                <Typography sx={valueSx}>{formatCost(tokenUsage.month.costCents)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={labelSx}>Calls</Typography>
                <Typography sx={valueSx}>{tokenUsage.month.calls}</Typography>
              </Box>
              {tokenUsage.total.calls > tokenUsage.month.calls && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)' }}>
                    All time
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>
                    {formatTokens(tokenUsage.total.totalTokens)} / {formatCost(tokenUsage.total.costCents)}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
